import React, { useState, useEffect, useCallback } from 'react';
import { ListGroup, Form, Button, Nav } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import LoadingSpinner from './LoadingSpinner';
import PatientTab from '../PatientTab';
import AppointmentsCalendar from './AppointmentsCalendar';
import { effectiveAppointmentType, listRowKey } from '../utils/effectiveAppointmentType';
import { prefetchRecommendationsNav, prefetchJointDecisionsGlobals, extendPatientListPrefetch, setStudyEnrollmentDeclined } from '../api';
import { prefetchListKindForPatient } from '../utils/patientPrefetchOrder';
import { patientSurgeryDate } from '../utils/patientSurgeryDate';
import { pipelineForStudy } from '../studies/studyPipeline';
import { ENROLLMENT_STUDIES } from '../studies/studyEligibility';
import StudyPipelineList from './StudyPipelineList';
import { runAfterPaint } from '../utils/runAfterPaint';
import { flushOpenDemoCharts } from '../studies/studyChartStore';
import './AppointmentsCalendar.css';

function listRowStyle(patient, { jointDecisions = false, past = false } = {}) {
  const status = jointDecisions ? patient.jd_approval_status : patient.approval_status;
  let backgroundColor = '';
  if (status === 'approved') backgroundColor = 'var(--action-approve)';
  else if (status === 'denied') backgroundColor = 'var(--action-deny)';
  else if (past) backgroundColor = '#f8f9fa';
  return {
    backgroundColor,
    ...(past ? { opacity: 0.8 } : {}),
  };
}

function PatientsListTab({ 
  allPatients, 
  currentPatient, 
  loadCurrentPatient,
  showCompass = false,
  study = '',
  onPatientApproval,
  showSurgeryOnly = false,
  onGoToMedManagement,
  onOfferStudyConsent,
  studyHandoff = null,
  onPatientListRefresh,
  onJdDecisionApproved,
  blurPatientNames = false,
  onBlurPatientNamesChange,
  isPatientListLoading = false,
  onStudyChange,
  pipelineReload = 0,
  enrollmentSurgeryPrompt = 0,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [listTab, setListTab] = useState('upcoming'); // 'past' | 'upcoming' - used for both Med Management and Joint Decisions
  const [pipelinePatients, setPipelinePatients] = useState([]);
  const [enrollmentPatch, setEnrollmentPatch] = useState(null);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');
  const [homeCalendarReady, setHomeCalendarReady] = useState(false);

  const toggleBlurPatientNames = () => {
    onBlurPatientNamesChange?.(!blurPatientNames);
  };

  // Log when patients are rendered
  useEffect(() => {
    if (allPatients && allPatients.length > 0) {
      const renderTime = performance.now();
      console.log('⏱️ [PERFORMANCE] Patient records displayed in UI');
      console.log('⏱️ [PERFORMANCE] Patients rendered:', allPatients.length);
    }
  }, [allPatients]);

  // Show patient list (not ToC) when no patient selected
  useEffect(() => {
    if (!currentPatient) setIsCollapsed(false);
  }, [currentPatient]);

  useEffect(() => {
    if (currentPatient?.id) {
      setSelectedPatientId(listRowKey(currentPatient));
    } else {
      setSelectedPatientId(null);
    }
  }, [currentPatient?.id, currentPatient?.appointment_id]);

  useEffect(() => {
    if (showSurgeryOnly) return undefined;
    const patientId = allPatients.find((p) => p?.id)?.id;
    if (!patientId) return undefined;
    return runAfterPaint(() => {
      prefetchJointDecisionsGlobals(patientId);
    });
  }, [showSurgeryOnly, allPatients]);

  // Keep the calendar mounted after first paint so incoming patients do not unmount it.
  useEffect(() => {
    if (currentPatient || homeCalendarReady) return undefined;
    return runAfterPaint(() => {
      setHomeCalendarReady(true);
    });
  }, [currentPatient, homeCalendarReady]);

  // Filter and sort patients
  const filteredPatients = allPatients
    .filter(patient => patient.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(patient => {
      const aptType = effectiveAppointmentType(patient);
      if (showSurgeryOnly) {
        return aptType === 'SURGERY';
      }
      return aptType !== 'SURGERY'; // non-SURGERY (includes null/undefined)
    })
    .sort((a, b) => new Date(patientSurgeryDate(a) || 0) - new Date(patientSurgeryDate(b) || 0));

  // Past = surgery date is before today (date-only; appointments stay upcoming for the full day)
  const isPastDue = (patientOrDate) => {
    const surgeryDate = typeof patientOrDate === 'string' || patientOrDate == null
      ? patientOrDate
      : patientSurgeryDate(patientOrDate);
    if (!surgeryDate) return false;
    const today = new Date();
    const surgery = new Date(surgeryDate);
    
    // Set both dates to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    surgery.setHours(0, 0, 0, 0);
    
    // Only consider it past due if surgery date is before today (not including today)
    return surgery < today;
  };

  // Categorization
  const pastDuePatients = filteredPatients
    .filter(patient => isPastDue(patient))
    .sort((a, b) => {
      const dateA = new Date(patientSurgeryDate(a) || 0);
      const dateB = new Date(patientSurgeryDate(b) || 0);
      return dateB - dateA; // Most recent first (descending order)
    });

  // Pending = not past due, action required and null approval status
  const pendingPatients = filteredPatients.filter(patient => 
    !isPastDue(patient) && 
    patient.has_medication_actions === true && 
    patient.approval_status === null
  );

  // Upcoming = not past due, no action required OR (action required but approval status is not null)
  const upcomingPatients = filteredPatients.filter(patient => 
    !isPastDue(patient) && 
    (
      patient.has_medication_actions === false || 
      (patient.has_medication_actions === true && patient.approval_status !== null)
    )
  );

  console.log('Past Due:', pastDuePatients.length, 'Upcoming:', upcomingPatients.length, 'Pending:', pendingPatients.length);

  const handlePatientClick = useCallback(async (patientId, appointmentId) => {
    const sameOpenPatient = currentPatient?.id === patientId;
    flushOpenDemoCharts();
    setSelectedPatientId(listRowKey({ id: patientId, appointment_id: appointmentId }));
    if (sameOpenPatient) return;
    setIsLoading(true);
    prefetchRecommendationsNav(patientId);
    const row = allPatients.find((patient) => {
      if (patient.id !== patientId) return false;
      if (appointmentId == null || appointmentId === '') return true;
      return patient.appointment_id != null && String(patient.appointment_id) === String(appointmentId);
    }) || allPatients.find((patient) => patient.id === patientId);
    extendPatientListPrefetch(
      allPatients,
      row ? prefetchListKindForPatient(row) : (showSurgeryOnly ? 'mm' : 'jd'),
      patientId,
    );
    try {
      await loadCurrentPatient(patientId, appointmentId);
    } finally {
      setIsLoading(false);
    }
  }, [allPatients, currentPatient?.id, showSurgeryOnly, loadCurrentPatient]);

  const handleDecideComplete = async (patientId, appointmentId) => {
    flushOpenDemoCharts();
    onJdDecisionApproved?.(patientId);
    setSelectedPatientId(listRowKey({ id: patientId, appointment_id: appointmentId }));
    await onPatientListRefresh?.();
    await loadCurrentPatient(patientId, appointmentId);
  };

  const isListRowSelected = (patient) => selectedPatientId === listRowKey(patient);

  const formatSurgeryDate = (patient) => {
    const dateStr = patientSurgeryDate(patient);
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return (
      <>
        {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        <br />
        <span style={{ paddingLeft: '0.5em' }}>{d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
      </>
    );
  };

  const patientListNameStyle = blurPatientNames
    ? { filter: 'blur(7px)', userSelect: 'none', WebkitUserSelect: 'none' }
    : undefined;

  const handleTogglePatientList = () => {
    setIsCollapsed((value) => !value);
  };

  const handleJointDecisionsEngage = () => {
    setIsCollapsed(true);
  };

  useEffect(() => {
    setWithdrawError('');
  }, [currentPatient?.id, study]);

  const studyWithdrawn = pipelinePatients.some(
    (row) => String(row.id) === String(currentPatient?.id) && row.declined
  );

  const handleWithdrawFromStudy = async () => {
    if (!currentPatient?.id || !study || studyWithdrawn) return;
    setWithdrawBusy(true);
    setWithdrawError('');
    setEnrollmentPatch({ patientId: currentPatient.id, declined: true, at: Date.now() });
    try {
      await setStudyEnrollmentDeclined(currentPatient.id, study, true);
    } catch (withdrawFailure) {
      setEnrollmentPatch({ patientId: currentPatient.id, declined: false, at: Date.now() });
      setWithdrawError(withdrawFailure?.message || 'Could not update this enrollment.');
    } finally {
      setWithdrawBusy(false);
    }
  };

  const sidePaneWidth = currentPatient && isCollapsed ? '3.25rem' : '25%';
  const mainPaneWidth = currentPatient && isCollapsed ? 'calc(100% - 3.25rem)' : '75%';
  const paneTransition =
    'flex-basis 0.5s cubic-bezier(0.4, 0, 0.2, 1), width 0.5s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1), padding-right 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
  const contentTransition =
    'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
  const showHomeCalendar = !currentPatient && homeCalendarReady;

  return (
    <div
      className="d-flex"
      style={{
        flex: '1 1 auto',
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        minHeight: 0,
        overflowX: 'hidden',
      }}
    >
      {/* Left side - All Patients List or Table of Contents */}
      <div 
        className="transition-all"
        style={{ 
          flex: `0 0 ${sidePaneWidth}`,
          width: sidePaneWidth,
          maxWidth: sidePaneWidth,
          minWidth: 0,
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          transition: paneTransition,
          overflow: 'hidden',
          paddingRight: currentPatient ? '0.35rem' : '1rem',
          overflowX: 'hidden'  // Prevent horizontal scrolling
        }}
      >
        <div className="d-flex flex-column h-100" style={{ overflowX: 'hidden' }}>
          {/* Fixed header section */}
          <div className="mb-3 d-flex align-items-center gap-2" style={{ overflowX: 'hidden', flexWrap: 'nowrap' }}>
            {currentPatient && (
              <Button
                variant="outline-secondary"
                onClick={handleTogglePatientList}
                style={{ minWidth: '2.4rem', width: '2.4rem', flexShrink: 0 }}
                className="d-flex align-items-center justify-content-center px-0"
                title={isCollapsed ? 'Show patient list' : 'Hide patient list'}
                aria-label={isCollapsed ? 'Show patient list' : 'Hide patient list'}
              >
                {isCollapsed ? '→' : '←'}
              </Button>
            )}
            <div
              className="d-flex align-items-center gap-2"
              style={{
                flex: 1,
                minWidth: 0,
                opacity: isCollapsed ? 0 : 1,
                transform: isCollapsed ? 'translateX(-0.5rem)' : 'translateX(0)',
                transition: contentTransition,
                pointerEvents: isCollapsed ? 'none' : 'auto',
                overflow: 'hidden',
              }}
              aria-hidden={isCollapsed}
            >
              <Form.Control
                type="search"
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ minWidth: 0, flex: 1 }}
                tabIndex={isCollapsed ? -1 : undefined}
              />
              <Button
                variant={blurPatientNames ? 'secondary' : 'outline-secondary'}
                onClick={toggleBlurPatientNames}
                style={{ minWidth: '40px', flexShrink: 0 }}
                className="d-flex align-items-center justify-content-center px-2"
                title={blurPatientNames ? 'Show patient names' : 'Blur patient names (privacy)'}
                aria-label={blurPatientNames ? 'Show patient names in list' : 'Blur patient names in list'}
                aria-pressed={blurPatientNames}
                tabIndex={isCollapsed ? -1 : undefined}
              >
                {blurPatientNames ? <Eye size={18} /> : <EyeSlash size={18} />}
              </Button>
            </div>
          </div>
          {showCompass && (
            <div
              className="mb-3"
              style={{
                display: isCollapsed ? 'none' : undefined,
              }}
              aria-hidden={isCollapsed}
            >
              <Nav variant="tabs" className="mb-0 px-2">
                {ENROLLMENT_STUDIES.map((item) => (
                  <Nav.Item key={item.id}>
                    <Nav.Link
                      active={study === item.id}
                      onClick={() => onStudyChange?.(item.id)}
                    >
                      {item.name}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>
          )}

          <div style={{ 
            overflowY: isCollapsed ? 'hidden' : 'auto', 
            overflowX: 'hidden',  // Prevent horizontal scrolling
            flex: 1, 
            minHeight: 0,
            position: 'relative' 
          }}>
            {/* Patient List with animation */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                transform: isCollapsed ? 'translateX(-0.75rem)' : 'translateX(0)',
                opacity: isCollapsed ? 0 : 1,
                transition: contentTransition,
                pointerEvents: isCollapsed ? 'none' : 'auto',
                willChange: 'transform, opacity',
              }}
              aria-hidden={isCollapsed}
            >
              {pipelineForStudy(study) ? (
                <StudyPipelineList
                  study={study}
                  searchTerm={searchTerm}
                  blurPatientNames={blurPatientNames}
                  selectedPatientId={selectedPatientId}
                  onSelect={handlePatientClick}
                  onPatients={setPipelinePatients}
                  enrollmentPatch={enrollmentPatch}
                  reloadToken={pipelineReload}
                />
              ) : isPatientListLoading || allPatients.length === 0 ? (
                <LoadingSpinner text="Loading patients..." />
              ) : showSurgeryOnly ? (
                /* Med Management: tabs for Past and Upcoming */
                <div>
                  <Nav variant="tabs" className="mb-2 px-2" activeKey={listTab} onSelect={(k) => setListTab(k || 'upcoming')}>
                    <Nav.Item>
                      <Nav.Link eventKey="upcoming">
                        Upcoming ({pendingPatients.length + upcomingPatients.length})
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="past">
                        Past ({pastDuePatients.length})
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  {listTab === 'past' ? (
                    <ListGroup>
                      {pastDuePatients.map((patient) => (
                        <ListGroup.Item 
                          key={listRowKey(patient)} 
                          active={isListRowSelected(patient)} 
                          onClick={() => handlePatientClick(patient.id, patient.appointment_id)} 
                          action 
                          className="d-flex align-items-center justify-content-between" 
                          style={listRowStyle(patient, { past: true })}
                        >
                          <div className="d-flex align-items-center">
                            <span style={patientListNameStyle}>{patient.name}</span>
                          </div>
                          <div className="d-flex align-items-center">
                            {patient.has_medication_actions === true ? (
                              <span className="badge bg-warning text-dark me-2" style={{ fontSize: '0.6rem' }}>MED</span>
                            ) : (
                              <span className="me-2" style={{ width: '24px' }}></span>
                            )}
                            {patientSurgeryDate(patient) && <span className="badge rounded-pill" style={{ backgroundColor: 'var(--brand-primary)', color: 'var(--brand-primary-dark)', fontSize: '0.75rem' }}>{formatSurgeryDate(patient)}</span>}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <div>
                      {pendingPatients.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-muted mb-2 px-3">PENDING APPROVAL</h6>
                          <ListGroup>
                            {pendingPatients.map((patient) => (
                              <ListGroup.Item key={listRowKey(patient)} active={isListRowSelected(patient)} onClick={() => handlePatientClick(patient.id, patient.appointment_id)} action className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <span style={patientListNameStyle}>{patient.name}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-warning text-dark me-2" style={{ fontSize: '0.6rem' }}>MED</span>
                                  {patientSurgeryDate(patient) && <span className="badge rounded-pill" style={{ backgroundColor: 'var(--brand-primary)', color: 'var(--brand-primary-dark)', fontSize: '0.75rem' }}>{formatSurgeryDate(patient)}</span>}
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </div>
                      )}
                      {upcomingPatients.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-muted mb-2 px-3">NO ACTION REQUIRED</h6>
                          <ListGroup>
                            {upcomingPatients.map((patient) => (
                              <ListGroup.Item key={listRowKey(patient)} active={isListRowSelected(patient)} onClick={() => handlePatientClick(patient.id, patient.appointment_id)} action className="d-flex align-items-center justify-content-between" style={listRowStyle(patient)}>
                                <div className="d-flex align-items-center">
                                  <span style={patientListNameStyle}>{patient.name}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  {patient.has_medication_actions === true ? (
                                    <span className="badge bg-warning text-dark me-2" style={{ fontSize: '0.6rem' }}>MED</span>
                                  ) : (
                                    <span className="me-2" style={{ width: '24px' }}></span>
                                  )}
                                  {patientSurgeryDate(patient) && <span className="badge rounded-pill" style={{ backgroundColor: 'var(--brand-primary)', color: 'var(--brand-primary-dark)', fontSize: '0.75rem' }}>{formatSurgeryDate(patient)}</span>}
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Joint Decisions: tabs to flip between Past and Upcoming */
                <div>
                  <Nav variant="tabs" className="mb-2 px-2" activeKey={listTab} onSelect={(k) => setListTab(k || 'upcoming')}>
                    <Nav.Item>
                      <Nav.Link eventKey="upcoming">
                        Upcoming ({filteredPatients.filter(p => !isPastDue(p)).length})
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="past">
                        Past ({filteredPatients.filter(p => isPastDue(p)).length})
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  {listTab === 'past' ? (
                    <ListGroup>
                      {filteredPatients
                        .filter(p => isPastDue(p))
                        .sort((a, b) => {
                          const dateA = new Date(patientSurgeryDate(a) || 0);
                          const dateB = new Date(patientSurgeryDate(b) || 0);
                          return dateB - dateA; // Most recent first
                        })
                        .map((patient) => (
                          <ListGroup.Item 
                            key={listRowKey(patient)} 
                            active={isListRowSelected(patient)} 
                            onClick={() => handlePatientClick(patient.id, patient.appointment_id)} 
                            action 
                            className="d-flex align-items-center justify-content-between" 
                            style={listRowStyle(patient, { jointDecisions: true, past: true })}
                          >
                            <div className="d-flex align-items-center">
                              <span style={patientListNameStyle}>{patient.name}</span>
                            </div>
                            <div className="d-flex align-items-center">
                              {patient.has_medication_actions === true ? (
                                <span className="badge bg-warning text-dark me-2" style={{ fontSize: '0.6rem' }}>MED</span>
                              ) : (
                                <span className="me-2" style={{ width: '24px' }}></span>
                              )}
                              {patientSurgeryDate(patient) && <span className="badge rounded-pill" style={{ backgroundColor: 'var(--brand-primary)', color: 'var(--brand-primary-dark)', fontSize: '0.75rem' }}>{formatSurgeryDate(patient)}</span>}
                            </div>
                          </ListGroup.Item>
                        ))}
                    </ListGroup>
                  ) : (
                    <ListGroup>
                      {filteredPatients
                        .filter(p => !isPastDue(p))
                        .map((patient) => (
                          <ListGroup.Item 
                            key={listRowKey(patient)} 
                            active={isListRowSelected(patient)} 
                            onClick={() => handlePatientClick(patient.id, patient.appointment_id)} 
                            action 
                            className="d-flex align-items-center justify-content-between" 
                            style={listRowStyle(patient, { jointDecisions: true })}
                          >
                            <div className="d-flex align-items-center">
                              <span style={patientListNameStyle}>{patient.name}</span>
                            </div>
                            <div className="d-flex align-items-center">
                              {patient.has_medication_actions === true ? (
                                <span className="badge bg-warning text-dark me-2" style={{ fontSize: '0.6rem' }}>MED</span>
                              ) : (
                                <span className="me-2" style={{ width: '24px' }}></span>
                              )}
                              {patientSurgeryDate(patient) && <span className="badge rounded-pill" style={{ backgroundColor: 'var(--brand-primary)', color: 'var(--brand-primary-dark)', fontSize: '0.75rem' }}>{formatSurgeryDate(patient)}</span>}
                            </div>
                          </ListGroup.Item>
                        ))}
                    </ListGroup>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right side */}
      <div
        id={currentPatient ? 'patient-details-container' : undefined}
        className={!currentPatient ? 'main-pane--calendar-home' : undefined}
        style={{ 
        flex: `0 0 ${mainPaneWidth}`,
        width: mainPaneWidth,
        maxWidth: mainPaneWidth,
        minWidth: 0,
        height: '100%', 
        minHeight: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        transition: paneTransition,
        overflowY: showCompass && currentPatient?.id ? 'hidden' : 'auto',
        overflowX: 'hidden',
        overflowAnchor: 'none',
      }}
      >
        {isLoading ? (
          <LoadingSpinner text="Loading patient details..." />
        ) : currentPatient ? (
            <PatientTab
              patient={currentPatient}
              onBack={null}
              onPatientApproval={onPatientApproval}
              showSurgeryOnly={showSurgeryOnly}
              showCompass={showCompass}
              study={study}
              studyHandoff={studyHandoff}
              onJointDecisionsEngage={handleJointDecisionsEngage}
              onGoToMedManagement={onGoToMedManagement}
              onOfferStudyConsent={onOfferStudyConsent}
              onDecideComplete={handleDecideComplete}
              blurPatientNames={blurPatientNames}
              studyWithdrawn={studyWithdrawn}
              withdrawBusy={withdrawBusy}
              withdrawError={withdrawError}
              onWithdrawFromStudy={showCompass ? handleWithdrawFromStudy : null}
              enrollmentSurgeryPrompt={enrollmentSurgeryPrompt}
            />
        ) : (
          <div className="calendar-home-stage">
            {showHomeCalendar && (
              <AppointmentsCalendar
                allPatients={allPatients}
                onSelectPatient={handlePatientClick}
                maskPatientNames={blurPatientNames}
                onMaskPatientNamesChange={onBlurPatientNamesChange}
              />
            )}
            {isPatientListLoading && (
              <div className="calendar-home-loading" aria-live="polite">
                <LoadingSpinner text="Loading patients..." />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PatientsListTab; 